import type {
  BillingTier,
  SubscriptionDetails,
} from "schemas/generated/billing";

import React from "react";

import Target from "@svg/billing/Target";

import { Button } from "@replo/design-system/components/button/Button";
import { analytics } from "@v2/features/infra/analytics/tracking";
import { MAX_ADDITIONAL_PUBLISHED_ELEMENT_BUNDLES } from "schemas/addOns";

import BillingIntervalToggle from "../shared/BillingIntervalToggle";
import {
  getCountOfAdditionalProjectsInSubscription,
  getCountOfAdditionalPublishedElementBundlesInSubscription,
  getCountOfAdditionalSitesInSubscription,
} from "../shared/sharedBillingUtils";
import UpsellCard from "../UpsellCard";
import PlanCard from "./PlanCard";

const PlanComparison = ({
  activeSubscription,
}: {
  activeSubscription?: SubscriptionDetails;
}) => {
  const currentAdditionalSites = React.useMemo(() => {
    if (!activeSubscription) {
      return 0;
    }

    return getCountOfAdditionalSitesInSubscription(activeSubscription);
  }, [activeSubscription]);

  const currentAdditionalProjects = React.useMemo(() => {
    if (!activeSubscription) {
      return 0;
    }

    return getCountOfAdditionalProjectsInSubscription(activeSubscription);
  }, [activeSubscription]);

  const [additionalSites, setAdditionalSites] = React.useState(
    currentAdditionalSites,
  );

  const [additionalProjects, setAdditionalProjects] = React.useState(
    currentAdditionalProjects,
  );

  const handleAdditionalSitesIncrement = () => {
    setAdditionalSites((prev) => prev + 1);
  };

  const handleAdditionalSitesDecrement = () => {
    setAdditionalSites((prev) => Math.max(0, prev - 1));
  };

  const handleAdditionalProjectsIncrement = () => {
    setAdditionalProjects((prev) => prev + 1);
  };

  const handleAdditionalProjectsDecrement = () => {
    setAdditionalProjects((prev) => Math.max(0, prev - 1));
  };

  // Common state
  const currentAdditionalPublishedElementBundles = React.useMemo(() => {
    if (!activeSubscription) {
      return 0;
    }

    // NOTE (Cole, 2025-09-03): Starter is the only plan that can have published item bundles
    if (activeSubscription?.billingTier !== "starter") {
      return 0;
    }

    return getCountOfAdditionalPublishedElementBundlesInSubscription(
      activeSubscription,
    );
  }, [activeSubscription]);

  const [
    additionalPublishedElementBundles,
    setAdditionalPublishedElementBundles,
  ] = React.useState<number>(currentAdditionalPublishedElementBundles);

  const handleAdditionalPublishedElementBundlesIncrement = () => {
    setAdditionalPublishedElementBundles((prev) => {
      return Math.min(prev + 1, MAX_ADDITIONAL_PUBLISHED_ELEMENT_BUNDLES);
    });
  };

  const handleAdditionalPublishedElementBundlesDecrement = () => {
    setAdditionalPublishedElementBundles((prev) => Math.max(0, prev - 1));
  };

  const isUserPayingOnMonthlyInterval =
    activeSubscription?.billingInterval === "monthly";

  const isUserOnYearlySubscription =
    activeSubscription?.billingInterval === "yearly";

  const [isYearly, setIsYearly] = React.useState(
    !isUserPayingOnMonthlyInterval,
  );

  return (
    <div className="flex flex-col gap-6 w-full">
      <div className="flex justify-between items-center">
        <span className="header-h5">
          Compare {isYearly ? "Annual" : "Monthly"} Plans
        </span>
        <BillingIntervalToggle
          isYearly={isYearly}
          setIsYearly={setIsYearly}
          isWorkspaceOnYearlySubscription={isUserOnYearlySubscription}
        />
      </div>
      {activeSubscription?.billingTier === "pro" && (
        <CustomUpsellCard activeSubscriptionBillingTier="pro" />
      )}

      <div className="flex">
        <PlanCard
          position="left"
          cardTier="starter"
          isYearly={isYearly}
          onIsYearlyChange={setIsYearly}
          additionalSites={additionalSites}
          additionalProjects={additionalProjects}
          additionalPublishedElementBundles={additionalPublishedElementBundles}
          currentBillingTier={activeSubscription?.billingTier ?? "free"}
          handleAdditionalSitesIncrement={handleAdditionalSitesIncrement}
          handleAdditionalSitesDecrement={handleAdditionalSitesDecrement}
          handleAdditionalProjectsIncrement={handleAdditionalProjectsIncrement}
          handleAdditionalProjectsDecrement={handleAdditionalProjectsDecrement}
          handleAdditionalPublishedElementBundlesIncrement={
            handleAdditionalPublishedElementBundlesIncrement
          }
          handleAdditionalPublishedElementBundlesDecrement={
            handleAdditionalPublishedElementBundlesDecrement
          }
          activeSubscription={activeSubscription}
        />
        <PlanCard
          position="right"
          cardTier="pro"
          isYearly={isYearly}
          onIsYearlyChange={setIsYearly}
          additionalSites={additionalSites}
          additionalProjects={additionalProjects}
          additionalPublishedElementBundles={additionalPublishedElementBundles}
          currentBillingTier={activeSubscription?.billingTier ?? "free"}
          handleAdditionalSitesIncrement={handleAdditionalSitesIncrement}
          handleAdditionalSitesDecrement={handleAdditionalSitesDecrement}
          handleAdditionalProjectsIncrement={handleAdditionalProjectsIncrement}
          handleAdditionalProjectsDecrement={handleAdditionalProjectsDecrement}
          handleAdditionalPublishedElementBundlesIncrement={
            handleAdditionalPublishedElementBundlesIncrement
          }
          handleAdditionalPublishedElementBundlesDecrement={
            handleAdditionalPublishedElementBundlesDecrement
          }
          activeSubscription={activeSubscription}
        />
      </div>
      {activeSubscription?.billingTier === "starter" && (
        <CustomUpsellCard activeSubscriptionBillingTier="starter" />
      )}
    </div>
  );
};

const CustomUpsellCard = ({
  activeSubscriptionBillingTier,
}: {
  activeSubscriptionBillingTier: BillingTier;
}) => {
  const QUINN_MEETING_LINK = "https://hubs.ly/Q03XH03g0";

  return (
    <UpsellCard
      title="Looking for more?"
      description="Chat with our team to get custom limits and dedicated support!"
      icon={<Target width={72} height={60} />}
      cta={
        <a href={QUINN_MEETING_LINK} target="_blank" rel="noreferrer">
          <Button
            variant="primary"
            size="base"
            onClick={() => {
              analytics.logEvent(
                "billing.billingPage.customPlanCallout.clicked",
                {
                  currentPlan: activeSubscriptionBillingTier,
                },
              );
            }}
          >
            <div className="flex items-center gap-2">
              <img
                src="/images/billing/quinn.png"
                alt="Book a Call"
                width={20}
                height={20}
              />
              Book a Call
            </div>
          </Button>
        </a>
      }
    />
  );
};

export default PlanComparison;
